import React, { useRef, useState } from "react";
import { Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import Table, { ASCEND } from "@components/Table";
import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { indexMyPersonalAccessTokensOperation as indexOperation } from "@api/services/identity";
import {
  type CreatedPersonalAccessToken,
  type PersonalAccessToken,
} from "@api/types";

import useColumnsConfig from "./helpers/useColumnsConfig";
import PersonalAccessTokenCreateModal, {
  type PersonalAccessTokenCreateModalHandle,
} from "./PersonalAccessTokenCreateModal";
import PersonalAccessTokenSecretModal from "./PersonalAccessTokenSecretModal";

const LABEL_TITLE = "Personal Access Tokens";
const LABEL_CREATE = "Create Token";
const DESCRIPTION = "Tokens let non-interactive clients act on your behalf. Each token carries your own access, so treat it like a password.";

type TableHandle = {
  addItem: (item: PersonalAccessToken) => void;
  updateItem: (id: string, attributes: Partial<PersonalAccessToken>) => void;
};

const PersonalAccessTokensTable = (): JSX.Element => {
  // NOTE: personal access tokens belong to the identity rather than to the
  //       organization membership, so they are indexed with the
  //       authentication token instead of the authorization one.
  const { authenticatedRequest } = useAppContext() as AppContext;

  const table = useRef<TableHandle>({} as TableHandle);
  const createModal = useRef<
    PersonalAccessTokenCreateModalHandle | Record<string, never>
  >({});

  const [ createdToken, setCreatedToken ] =
    useState<CreatedPersonalAccessToken>();

  const onCreated = (newToken: CreatedPersonalAccessToken) => {
    table.current.addItem(newToken);
    setCreatedToken(newToken);
  };

  const onRevoked = (revokedToken: PersonalAccessToken) =>
    table.current.updateItem(revokedToken.id, revokedToken);

  const closeSecretModal = () => setCreatedToken(undefined);

  const openCreateModal = () => createModal.current.open?.();

  const columnsConfig = useColumnsConfig({ onRevoked });

  const headerActions = [
    {
      icon: <PlusOutlined />,
      action: openCreateModal,
      title: LABEL_CREATE,
    },
  ];

  const tableProps = {
    size: "small" as const,
    table,
    request: authenticatedRequest,
    className: "last-column-action",
    headerActions,
    columnsConfig,
    indexOperation,
    defaultSortColumn: "name",
    defaultSortColumnDirection: ASCEND,
  };

  return (
    <>
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        {LABEL_TITLE}
      </Typography.Title>

      <Typography.Paragraph type="secondary">
        {DESCRIPTION}
      </Typography.Paragraph>

      <Table {...tableProps} />

      <PersonalAccessTokenCreateModal
        modal={createModal}
        onSuccess={onCreated}
      />

      <PersonalAccessTokenSecretModal
        isOpen={!!createdToken}
        name={createdToken?.name}
        secretToken={createdToken?.secretToken}
        onClose={closeSecretModal}
      />
    </>
  );
};

export default PersonalAccessTokensTable;
